import { Box, Stack, Typography } from '@mui/material';
import { useEffect, useState } from 'react';

import { Button } from '@/components/form/baseInputs/Button';
import { Copy01Icon } from '@/components/icons/Copy01Icon';
import { InfoCircleFilledIcon } from '@/components/icons/InfoCircleFilledIcon';
import { LinkExternal01Icon } from '@/components/icons/LinkExternal01Icon';
import { HeaderCard } from '@/components/layout/HeaderCard';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { useCopyToClipboard } from '@/hooks/useCopyToClipboard';
import { COLORS } from '@/styles/tokens/colors';
import { getFullyQualifiedTenantURL, getTenant } from '@/utils/tenantUtils';

const clients = {
  Claude: {
    destination: 'https://claude.ai/customize/connectors',
    setupTitle: 'Open Claude and add a custom connector',
    setupInstructions:
      'In Claude, go to Customize → Connectors → Add custom connector. Paste the Luminary server URL, then click Add.',
    finishInstructions:
      'Find Luminary in Connectors and click Connect. Sign in and approve access when Claude asks. Enable the connector in a chat with the + menu.',
    copyLabel: 'Copy server URL',
    openLabel: 'Open Claude connectors',
  },
  ChatGPT: {
    destination: 'https://chatgpt.com/plugins',
    setupTitle: 'Open ChatGPT and create an MCP app',
    setupInstructions:
      'Turn on Developer mode in ChatGPT settings. On the linked page, select + to create a developer-mode app. Paste the Luminary server URL as its endpoint.',
    finishInstructions:
      'Select OAuth, scan tools, and complete authorization. Create the app, then select it from the Developer mode tool in a chat. Your workspace may require admin approval.',
    copyLabel: 'Copy server URL',
    openLabel: 'Open ChatGPT apps',
  },
  Codex: {
    destination: 'https://developers.openai.com/codex/mcp',
    setupTitle: 'Run the command in your terminal',
    setupInstructions:
      'Open a terminal where the Codex CLI is installed. Paste the copied command and press Enter. The Codex app and IDE extension share this configuration.',
    finishInstructions:
      'Follow the authorization prompt from Codex and approve Luminary access. Then ask Codex to use Luminary tools.',
    copyLabel: 'Copy Codex command',
    openLabel: 'Read Codex setup guide',
  },
} as const;

type Client = keyof typeof clients;

function SetupStep({
  number,
  title,
  children,
  isLast = false,
}: React.PropsWithChildren<{
  number: number;
  title: string;
  isLast?: boolean;
}>) {
  return (
    <Stack direction="row" spacing={2} alignItems="stretch">
      <Stack
        alignItems="center"
        aria-hidden="true"
        sx={{ width: 32, flexShrink: 0 }}
      >
        <Box
          sx={{
            bgcolor: 'primary.main',
            borderRadius: '50%',
            color: 'primary.contrastText',
            height: 32,
            lineHeight: '32px',
            textAlign: 'center',
            width: 32,
          }}
        >
          {number}
        </Box>
        {!isLast && (
          <Box sx={{ bgcolor: 'divider', flexGrow: 1, my: 1, width: 2 }} />
        )}
      </Stack>
      <Stack
        spacing={1}
        alignItems="flex-start"
        sx={{ minWidth: 0, pt: 0.5, pb: isLast ? 0 : 3 }}
      >
        <Typography variant="body-s" fontWeight={600}>
          {title}
        </Typography>
        {children}
      </Stack>
    </Stack>
  );
}

export function McpSetupCard() {
  const [client, setClient] = useState<Client>('Claude');
  const [serverURL, setServerURL] = useState('');
  const [copyToClipboard] = useCopyToClipboard();
  const { createErrorFeedback, createSuccessFeedback } = useFeedback();
  useEffect(() => {
    setServerURL(`${getFullyQualifiedTenantURL(getTenant())}/mcp/v1`);
  }, []);
  const value =
    client === 'Codex'
      ? `codex mcp add luminary --url ${serverURL}`
      : serverURL;
  const selected = clients[client];

  return (
    <HeaderCard
      heading="Connect an AI assistant"
      subheading="Set up Luminary MCP in your preferred assistant"
    >
      <Stack>
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="flex-start"
          sx={{
            p: 2,
            mb: 3,
            bgcolor: COLORS.BLUE[50],
            border: 1,
            borderColor: COLORS.BLUE[100],
            borderRadius: 1,
          }}
        >
          <InfoCircleFilledIcon size={20} color={COLORS.BLUE[800]} />
          <Typography variant="body-xs">
            You will sign in to Luminary and approve access in the assistant you
            choose.
          </Typography>
        </Stack>
        <SetupStep number={1} title="Choose your AI assistant">
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {(Object.keys(clients) as Client[]).map((name) => (
              <Button
                key={name}
                size="sm"
                variant={client === name ? 'primary' : 'secondary'}
                aria-pressed={client === name}
                onClick={() => setClient(name)}
              >
                {name}
              </Button>
            ))}
          </Stack>
        </SetupStep>
        <SetupStep
          number={2}
          title={
            client === 'Codex'
              ? 'Copy the Codex command'
              : 'Copy the server URL'
          }
        >
          <Typography variant="body-xs">
            {client === 'Codex'
              ? 'Run this command on your computer, not in the browser.'
              : `Paste this URL into ${client} when it asks for an MCP server endpoint.`}
          </Typography>
          <Box
            sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}
          >
            <Box
              component="code"
              sx={{ overflowWrap: 'anywhere', fontSize: 13, userSelect: 'all' }}
            >
              {serverURL ? value : 'Preparing server URL…'}
            </Box>
          </Box>
          <Button
            size="sm"
            variant="primary"
            startIcon={Copy01Icon}
            aria-label={selected.copyLabel}
            disabled={!serverURL}
            onClick={() =>
              copyToClipboard(
                value,
                createSuccessFeedback('Copied to clipboard'),
                createErrorFeedback('Failed to copy to clipboard')
              )
            }
          >
            {selected.copyLabel}
          </Button>
        </SetupStep>
        <SetupStep number={3} title={selected.setupTitle}>
          <Typography variant="body-xs">
            {selected.setupInstructions}
          </Typography>
          <Button
            size="sm"
            variant="secondary"
            endIcon={LinkExternal01Icon}
            aria-label={selected.openLabel}
            href={selected.destination}
            external
            target="_blank"
            rel="noopener noreferrer"
          >
            {selected.openLabel}
          </Button>
        </SetupStep>
        <SetupStep
          number={4}
          title="Authorize Luminary and start using it"
          isLast
        >
          <Typography variant="body-xs">
            {selected.finishInstructions}
          </Typography>
        </SetupStep>
      </Stack>
    </HeaderCard>
  );
}
